import PropTypes from 'prop-types'
import ZoomIcon from 'shared/asstes/zoom.svg'
import Camera from 'shared/asstes/camera-todos.svg'
import BinIcon from 'shared/asstes/bin.svg'
import AnimatedBin from 'shared/asstes/bin_animated.svg'
import { Modal, Lightbox, ImageWithLoader } from 'shared/components'
import ScreenLoader from '../ScreenLoader'
import {
    ModalCont,
    ModalHeading,
    ModalBody,
    ModalFooter,
    CardModalCont,
    CardActionButton,
    CardActionButtonImg,
    ActionButton,
    ActionUploadButton,
    ReviewButton,
} from './Styles'
import { useState, useRef } from 'react'
import { convertBase64 } from 'shared/utils/converter'
import useSingleTransactions from 'shared/hooks/sell/single-transaction'
import api from 'shared/utils/api'
import toast from 'shared/utils/toast'
import alert from 'shared/utils/alert'

const defaultProps = {
    review: false,
    overviewQuickLink: false,
    title: 'Your Photos',
    btnText: 'Upload Photos',
    renderCustomLink: () => {},
}

const propTypes = {
    overviewQuickLink: PropTypes.bool,
    title: PropTypes.string,
    btnText: PropTypes.string,
    renderCustomLink: PropTypes.func,
}

const PhotosViewer = ({ id, review, overviewQuickLink, title, btnText, renderCustomLink }) => {
    const [images, setImages] = useState([])
    const [isWorking, setIsWorking] = useState(false)
    const $inputRef = useRef(null)
    const { fetch } = useSingleTransactions()

    const initImages = () => {
        setImages([])
        const transaction = window.currentTransaction
        if (transaction?.images?.length) {
            const temp = []
            transaction.images.forEach((image) => {
                temp.push({
                    id: image.imageId,
                    backgroundImage: image.imageUrl,
                    fileName: '',
                    fileType: '',
                    uploaded: true,
                    uploadError: false,
                    canDelete: false,
                })
            })
            setImages(temp)
        }
    }

    const handleBrowseClick = () => $inputRef.current && $inputRef.current.click()

    const resetInput = () => {
        if ($inputRef.current) {
            $inputRef.current.value = ''
        }
    }

    const handleOnFileSelect = async (e) => {
        const newFiles = e.target.files
        const temp = []
        for (var i = 0; i < newFiles.length; i++) {
            const base64 = await convertBase64(newFiles[i])
            const fileName = newFiles[i].name
            const fileType = newFiles[i].type
            const file = newFiles[i]
            temp.push({
                id: Math.floor(Date.now() + Math.random() * 1000),
                backgroundImage: base64,
                file,
                fileName,
                fileType,
                uploaded: false,
                canDelete: true,
            })
        }
        setImages([...images, ...temp])
        resetInput()
    }

    const handleImageDelete = async (data) => {
        const { uploaded, id } = data
        if (!uploaded) {
            setImages(images.filter((image) => image.id !== id))
        }
    }

    const handlePhotosUpload = async () => {
        const newImages = images.filter((image) => !image.uploaded)
        if (!newImages.length) return
        setIsWorking(true)
        try {
            for (const image of newImages) {
                await api.post(
                    `customer/v3/cash-offer-transactions/${window.currentTransaction?.transactionId}/upload-photo-v2`,
                    { file: image.file },
                    { 'Content-Type': 'multipart/form-data' },
                )
            }
            await fetch()
            initImages()
            toast.success('Images uploaded successfully.')
        } catch (e) {
            toast.error(e?.error?.message ?? 'Something went wrong. Unable to upload images.')
        } finally {
            setIsWorking(false)
        }
    }

    const renderImage = (image) => {
        return (
            <Lightbox
                image={image.backgroundImage || image}
                renderLink={({ open }) => (
                    <ImageWithLoader
                        image={image.backgroundImage || image}
                        className="card-photos"
                        renderOnTop={() => (
                            <div className="action-buttons">
                                {image.canDelete && (
                                    <CardActionButton onClick={() => handleImageDelete(image)}>
                                        <CardActionButtonImg src={BinIcon} alt="" />
                                    </CardActionButton>
                                )}
                                <CardActionButton primary onClick={open}>
                                    <CardActionButtonImg src={ZoomIcon} alt="" />
                                </CardActionButton>
                            </div>
                        )}
                    />
                )}
            />
        )
    }

    const renderDefaultScreen = () => {
        return (
            <div className="default-screen">
                <img src={Camera} alt="" />
                <p>No photos uploaded.</p>
            </div>
        )
    }

    const hasPendingUploads = images.some((image) => !image.uploaded)

    return (
        <Modal
            testid="modal:PhotosViewer"
            width="875px"
            backgroundColor="white"
            withCloseIcon={true}
            variableWidth={true}
            onOpenEvent={initImages}
            renderLink={(modal) => {
                return review ? (
                    <ReviewButton id={id} onClick={modal.open}>
                        {btnText}
                    </ReviewButton>
                ) : overviewQuickLink ? (
                    renderCustomLink(modal)
                ) : (
                    <ActionButton id={id} variant="primary" onClick={modal.open}>
                        {btnText}
                    </ActionButton>
                )
            }}
            renderContent={(modal) => (
                <ModalCont>
                    <ScreenLoader sL={isWorking} full={false} />
                    {!review && (
                        <input
                            type="file"
                            id="file-select"
                            ref={$inputRef}
                            multiple
                            style={{ display: 'none' }}
                            onChange={handleOnFileSelect}
                        />
                    )}
                    <ModalHeading>
                        {title} {images?.length > 0 && `(${images.length})`}
                    </ModalHeading>
                    <ModalBody id="card-body" defaultScreen={!images?.length}>
                        {images?.length
                            ? images.map((image, i) => (
                                  <CardModalCont key={Date.now() + i}>
                                      {renderImage(image)}
                                  </CardModalCont>
                              ))
                            : renderDefaultScreen()}
                    </ModalBody>
                    {!review && (
                        <ModalFooter>
                            <ActionButton
                                bordered
                                onClick={handleBrowseClick}
                                disabled={isWorking}
                            >
                                Browse
                            </ActionButton>
                            <ActionUploadButton
                                variant="primary"
                                onClick={handlePhotosUpload}
                                disabled={!hasPendingUploads || isWorking}
                                style={{ marginTop: 0 }}
                            >
                                Upload Photos
                            </ActionUploadButton>
                        </ModalFooter>
                    )}
                </ModalCont>
            )}
        />
    )
}

PhotosViewer.defaultProps = defaultProps
PhotosViewer.propTypes = propTypes

export default PhotosViewer
